'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef } from 'react';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { Loader } from '@/app/components/ui/loader';
import { FALLBACK_CLAW_THEME, HOME_PAGE } from '@/app/constants';
import { useClawAccess, useIsScreen } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { cn } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import { useClawPassExpire } from '../_hook/use-claw-pass-expire';
import { useInitClaw } from '../_hook/use-init-claw';
import { useOpenPurchaseDialog } from '../_hook/use-open-purchase-dialog';
import { ExpireRetainDialog } from './ExpireRetainDialog';
import { Nav } from './Nav';
import { PayClawPassTooltip } from './PayClawPassTooltip';

// claw 路径离开时把 <html> 上的主题属性还原成 vidu-next 其它页面默认的 dark，避免 next-themes 把 light 状态遗留出去。
const useResetDocumentThemeOnUnmount = () => {
  useEffect(() => {
    return () => {
      const root = document.documentElement;
      root.dataset.theme = FALLBACK_CLAW_THEME;
      root.classList.toggle('light', FALLBACK_CLAW_THEME === 'light');
      root.classList.toggle('dark', FALLBACK_CLAW_THEME === 'dark');
      root.style.colorScheme = FALLBACK_CLAW_THEME;
    };
  }, []);
};

const ClawLayoutContent = ({ children }: { children: React.ReactNode }) => {
  const t = useTranslations();
  const { isSmScreen } = useIsScreen();
  const { isSidebarCollapsed, toggleClawSidebar } = useStore((state) => ({
    isSidebarCollapsed: state.clawSidebarCollapsed,
    toggleClawSidebar: state.toggleClawSidebar,
  }));

  // 仅为副作用调用：claw 准入流程（路由守卫 + provisioning 轮询兜底）。
  // 开通 / 激活过程的视觉反馈统一收敛到发送按钮内联 loading，这里不再出整页 / 遮罩 loading。
  useInitClaw();
  const { access } = useClawAccess();
  const openPurchaseDialog = useOpenPurchaseDialog();
  const { withinAlertWindow, useHours, days: expireDays, hours: expireHours } = useClawPassExpire();

  // 小屏模式进入时自动折叠；用 ref 读取最新值，避免用户手动展开后被立即再折叠回去。
  const isSidebarCollapsedRef = useRef(isSidebarCollapsed);
  isSidebarCollapsedRef.current = isSidebarCollapsed;

  useEffect(() => {
    if ((isSmScreen || access === 'entitled') && !isSidebarCollapsedRef.current) {
      toggleClawSidebar(true);
    }
  }, [isSmScreen, access]);

  return (
    <div className="flex h-screen overflow-hidden bg-[#0A0D10] light:bg-system-bg01">
      <div
        className={cn(
          'overflow-hidden transition-[width] duration-200 ease-out',
          isSidebarCollapsed ? 'w-0' : 'w-[264px]',
        )}
      >
        <Nav onToggleCollapse={toggleClawSidebar} />
      </div>
      <div className="flex h-screen max-h-screen min-h-0 flex-1 flex-col overflow-hidden bg-[#F4F4FA] pt-2 dark:bg-[#0A0D10]">
        <PayClawPassTooltip />
        <ExpireRetainDialog />
        {withinAlertWindow && (
          <div className="mx-4 flex items-center gap-2 rounded-8 bg-[rgba(255,165,0,0.12)] px-4 py-2 text-sm">
            <BaseLineExclamation className="size-4 shrink-0" />
            <span className="flex-1">
              {useHours
                ? t('claw.layoutShell.expireBannerHours', { hours: expireHours })
                : t('claw.layoutShell.expireBanner', { days: expireDays })}
            </span>
            <button
              type="button"
              onClick={() => openPurchaseDialog('claw_page_my_purchase_advance')}
              className="shrink-0 rounded-full bg-white px-3 py-1 font-medium text-black text-xs transition-opacity hover:opacity-90"
            >
              {t('payments.plans.passAdvanceButton')}
            </button>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-hidden">{children}</div>
      </div>
    </div>
  );
};

export const ClawLayoutClientShell = ({ children }: { children: React.ReactNode }) => {
  useResetDocumentThemeOnUnmount();
  const router = useRouter();
  const { isTeamSpace, isUserInfoInitialized } = useStore((state) => ({
    isTeamSpace: state.isTeamSpace,
    isUserInfoInitialized: state.isUserInfoInitialized,
  }));

  // 团队空间下屏蔽 claw，直接访问 URL 时重定向到首页。
  // 守卫放在外层避免 ClawLayoutContent 里的 useInitClaw 触发不必要的 claw 实例创建。
  useEffect(() => {
    if (isUserInfoInitialized && isTeamSpace) {
      router.replace(HOME_PAGE);
    }
  }, [isUserInfoInitialized, isTeamSpace, router]);

  if (isTeamSpace) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0A0D10] light:bg-system-bg01">
        <Loader />
      </div>
    );
  }

  return <ClawLayoutContent>{children}</ClawLayoutContent>;
};
